import React, { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { addDoc, collection } from 'firebase/firestore';
import toast from 'react-hot-toast';
import {
  FiCheck,
  FiChevronDown,
  FiCreditCard,
  FiLock,
  FiTruck
} from 'react-icons/fi';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { db } from '../utils/firebase';
import { processCheckout } from '../utils/paymentService';
import CashIcon from '../components/CashIcon';
import { useHeroReveal, useScrollReveal } from '../hooks/useGsapMotion';

const formatCurrency = (value) => `BDT ${Number(value || 0).toLocaleString()}`;

const Checkout = () => {
  const { items, getCartTotal, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const pageRef = useRef(null);

  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: user?.email || '',
    streetAddress: '',
    city: '',
    area: '',
    saveInfo: false
  });
  const [paymentMethod, setPaymentMethod] = useState('sslcommerz');
  const [loading, setLoading] = useState(false);

  useHeroReveal(pageRef, [items.length]);
  useScrollReveal(pageRef, [items.length, paymentMethod]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user) {
      toast.error('Please login to continue');
      navigate('/login');
      return;
    }

    if (items.length === 0) {
      toast.error('Your cart is empty');
      navigate('/cart');
      return;
    }

    if (!formData.fullName || !formData.phone || !formData.streetAddress || !formData.city || !formData.area) {
      toast.error('Please complete your shipping details');
      return;
    }

    try {
      setLoading(true);

      const nameParts = formData.fullName.trim().split(/\s+/);
      const firstName = nameParts[0] || formData.fullName;
      const lastName = nameParts.slice(1).join(' ') || '-';
      const fullAddress = `${formData.streetAddress}, ${formData.area}, ${formData.city}`;

      const orderData = {
        userId: user.uid,
        items,
        total: getCartTotal(),
        shippingAddress: {
          fullName: formData.fullName,
          firstName,
          lastName,
          streetAddress: formData.streetAddress,
          apartment: formData.area,
          city: formData.city,
          phone: formData.phone,
          email: formData.email,
          saveInfo: formData.saveInfo
        },
        customerInfo: {
          name: formData.fullName,
          phone: formData.phone,
          email: formData.email,
          address: fullAddress
        },
        paymentMethod,
        status: 'pending',
        createdAt: new Date(),
        orderNumber: `ORD-${Date.now()}`
      };

      const orderRef = await addDoc(collection(db, 'orders'), orderData);

      if (paymentMethod === 'cash') {
        toast.success('Order placed successfully');
        clearCart();
        navigate(`/order-confirmation/${orderRef.id}`);
        return;
      }

      const checkoutData = {
        orderId: orderRef.id,
        amount: getCartTotal(),
        paymentMethod,
        customerInfo: {
          name: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          address: fullAddress,
          city: formData.city,
          id: user.uid
        },
        items,
        orderReference: orderData.orderNumber
      };

      const result = await processCheckout(checkoutData, { paymentMethod });
      if (!result.success) {
        throw new Error(result.message || 'Failed to initiate payment');
      }

      window.location.href = result.checkout_url || result.payment_url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.message || 'Failed to place order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const subtotal = getCartTotal();
  const shipping = subtotal > 0 ? 120 : 0;
  const total = subtotal + shipping;

  return (
    <div ref={pageRef} className="sc-container py-8 md:py-10">
      <div className="mb-8 flex items-center justify-between gap-4" data-hero-item>
        <h1 className="text-2xl font-black text-[#1A1A1A] md:text-3xl">Secure Checkout</h1>
        <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-slate-600">
          <FiLock size={14} className="text-green-600" />
          SSL Protected
        </div>
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
        <div className="flex flex-col gap-8 lg:col-span-8">
          <section data-hero-item>
            <ol className="flex items-center">
              <li className="relative pr-10 sm:pr-20">
                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                  <div className="h-0.5 w-full bg-[#DB4444]" />
                </div>
                <span className="relative flex h-8 w-8 items-center justify-center rounded-full bg-[#DB4444] text-white">
                  <FiCheck size={13} />
                </span>
                <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs font-semibold text-[#DB4444]">Cart</span>
              </li>
              <li className="relative pr-10 sm:pr-20">
                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                  <div className="h-0.5 w-full bg-slate-200" />
                </div>
                <span className="relative flex h-8 w-8 items-center justify-center rounded-full border-2 border-[#DB4444] bg-[#DB4444] text-sm font-bold text-white">
                  2
                </span>
                <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs font-semibold text-[#DB4444]">
                  Details
                </span>
              </li>
              <li className="relative">
                <span className="relative flex h-8 w-8 items-center justify-center rounded-full border-2 border-slate-300 bg-white text-sm font-medium text-slate-500">
                  3
                </span>
                <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs font-medium text-slate-500">Done</span>
              </li>
            </ol>
          </section>

          <section className="sc-soft-card p-6 sm:p-8" data-reveal>
            <div className="mb-6 flex items-center justify-between gap-4">
              <h2 className="flex items-center gap-2 text-xl font-black text-[#1A1A1A]">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#DB4444]/10 text-xs font-bold text-[#DB4444]">
                  1
                </span>
                Shipping Details
              </h2>
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="sc-hover-smooth text-sm font-medium text-[#DB4444] hover:underline"
              >
                Log in for faster checkout
              </button>
            </div>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <FloatingInput
                className="md:col-span-2"
                id="fullName"
                name="fullName"
                label="Full Name"
                value={formData.fullName}
                onChange={handleChange}
                required
              />
              <FloatingInput
                id="phone"
                name="phone"
                label="Phone Number"
                value={formData.phone}
                onChange={handleChange}
                required
              />
              <FloatingInput
                id="email"
                name="email"
                label="Email Address"
                value={formData.email}
                onChange={handleChange}
                type="email"
              />
              <FloatingInput
                className="md:col-span-2"
                id="streetAddress"
                name="streetAddress"
                label="Street Address (House, Road, Block)"
                value={formData.streetAddress}
                onChange={handleChange}
                required
              />
              <FloatingSelect
                id="city"
                name="city"
                label="City / District"
                value={formData.city}
                onChange={handleChange}
                options={['Dhaka', 'Chittagong', 'Sylhet', 'Khulna']}
                required
              />
              <FloatingInput
                id="area"
                name="area"
                label="Thana / Area"
                value={formData.area}
                onChange={handleChange}
                required
              />
            </div>

            <label className="mt-5 inline-flex items-center gap-2 text-sm text-slate-600">
              <input
                type="checkbox"
                name="saveInfo"
                checked={formData.saveInfo}
                onChange={handleChange}
                className="h-4 w-4 rounded border-slate-300 text-[#DB4444] focus:ring-[#DB4444]"
              />
              Save this information for next checkout
            </label>
          </section>

          <section className="sc-soft-card p-6 sm:p-8" data-reveal>
            <h2 className="mb-6 flex items-center gap-2 text-xl font-black text-[#1A1A1A]">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#DB4444]/10 text-xs font-bold text-[#DB4444]">
                2
              </span>
              Payment Method
            </h2>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <label
                className={`sc-hover-smooth relative flex cursor-pointer items-start rounded-xl border bg-white p-4 shadow-sm hover:scale-[1.01] ${
                  paymentMethod === 'cash'
                    ? 'border-[#1A1A1A] ring-1 ring-[#1A1A1A]'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <input
                  type="radio"
                  name="paymentMethod"
                  value="cash"
                  checked={paymentMethod === 'cash'}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="sr-only"
                />
                <div className="flex flex-1 items-start gap-3">
                  <CashIcon className="mt-0.5 h-5 w-5" />
                  <div>
                    <p className="text-sm font-bold text-[#1A1A1A]">Cash on Delivery</p>
                    <p className="mt-1 text-sm text-slate-500">Pay when you receive.</p>
                  </div>
                </div>
                {paymentMethod === 'cash' && <FiCheck className="text-[#1A1A1A]" />}
              </label>

              <label
                className={`sc-hover-smooth relative flex cursor-pointer items-start rounded-xl border bg-white p-4 shadow-sm hover:scale-[1.01] ${
                  paymentMethod === 'sslcommerz'
                    ? 'border-[#DB4444] ring-1 ring-[#DB4444]'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <input
                  type="radio"
                  name="paymentMethod"
                  value="sslcommerz"
                  checked={paymentMethod === 'sslcommerz'}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="sr-only"
                />
                <div className="flex flex-1 items-start gap-3">
                  <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#DB4444]/10 text-[#DB4444]">
                    <FiCreditCard size={13} />
                  </span>
                  <div>
                    <p className="text-sm font-bold text-[#DB4444]">SSLCommerz</p>
                    <p className="mt-1 text-sm text-slate-500">Safe digital payment.</p>
                  </div>
                </div>
                {paymentMethod === 'sslcommerz' && <FiCheck className="text-[#DB4444]" />}
              </label>
            </div>
          </section>

          <section className="rounded-lg border border-slate-100 bg-slate-50 p-4" data-reveal>
            <div className="flex items-start gap-3">
              <FiTruck className="mt-0.5 text-slate-600" />
              <p className="text-sm text-slate-600">
                Expected delivery window: <span className="font-semibold text-[#DB4444]">2-3 business days</span>
              </p>
            </div>
          </section>
        </div>

        <aside className="lg:col-span-4">
          <div className="space-y-6 lg:sticky lg:top-24" data-hero-item>
            <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg">
              <div className="border-b border-slate-100 p-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold text-[#1A1A1A]">Order Summary</h3>
                  <Link to="/cart" className="sc-hover-smooth text-sm font-medium text-[#DB4444] hover:underline">
                    Edit Cart
                  </Link>
                </div>
              </div>

              <ul className="max-h-64 divide-y divide-slate-100 overflow-y-auto px-6">
                {items.map((item) => (
                  <li key={item.id} className="flex py-4">
                    <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-md border border-slate-200 bg-slate-100">
                      <img
                        src={item.image || item.imageUrl || '/placeholder-product.jpg'}
                        alt={item.name}
                        className="h-full w-full object-cover"
                      />
                    </div>
                    <div className="ml-4 flex flex-1 flex-col">
                      <div className="flex items-start justify-between text-sm">
                        <h4 className="line-clamp-1 font-semibold text-[#1A1A1A]">{item.name}</h4>
                        <p className="ml-3 font-semibold">{formatCurrency(item.price * item.quantity)}</p>
                      </div>
                      <p className="mt-1 text-xs text-slate-500">Qty {item.quantity}</p>
                    </div>
                  </li>
                ))}
              </ul>

              <div className="bg-slate-50 p-6">
                <div className="mb-2 flex items-center justify-between text-sm">
                  <p className="text-slate-600">Subtotal</p>
                  <p className="font-medium text-[#1A1A1A]">{formatCurrency(subtotal)}</p>
                </div>
                <div className="mb-4 flex items-center justify-between border-b border-slate-200 pb-4 text-sm">
                  <p className="text-slate-600">Shipping</p>
                  <p className="font-medium text-[#1A1A1A]">{formatCurrency(shipping)}</p>
                </div>
                <div className="mb-6 flex items-center justify-between">
                  <p className="text-base font-bold text-[#1A1A1A]">Total</p>
                  <p className="text-xl font-bold text-[#DB4444]">{formatCurrency(total)}</p>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="group sc-hover-smooth relative flex w-full justify-center rounded-lg bg-[#DB4444] px-4 py-3.5 text-sm font-bold text-white shadow-sm hover:-translate-y-0.5 hover:bg-[#c93e3e] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                    <FiLock className="sc-hover-smooth group-hover:scale-105" />
                  </span>
                  {loading ? 'Processing...' : 'Place Order'}
                </button>

                <div className="mt-4 flex items-center justify-center gap-2 text-xs text-slate-500">
                  <FiCheck size={13} />
                  <span>SSL Encrypted Transaction</span>
                </div>
              </div>
            </section>

            <div className="flex justify-center gap-3 opacity-60 grayscale">
              <div className="h-6 w-10 rounded bg-slate-300" />
              <div className="h-6 w-10 rounded bg-slate-300" />
              <div className="h-6 w-10 rounded bg-slate-300" />
            </div>
          </div>
        </aside>
      </form>
    </div>
  );
};

const FloatingInput = ({ id, name, label, className = '', onChange, value, type = 'text', required = false }) => (
  <label className={`relative block ${className}`}>
    <input
      id={id}
      name={name}
      type={type}
      placeholder=" "
      value={value}
      onChange={onChange}
      required={required}
      className="peer block h-14 w-full rounded-lg border border-slate-300 bg-transparent px-4 pb-2.5 pt-6 text-sm text-[#1A1A1A] outline-none transition-colors duration-500 focus:border-[#DB4444]"
    />
    <span className="pointer-events-none absolute left-4 top-4 z-10 origin-[0] -translate-y-4 scale-75 transform text-xs font-semibold text-slate-500 transition-all duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-placeholder-shown:text-sm peer-placeholder-shown:font-medium peer-focus:-translate-y-4 peer-focus:scale-75 peer-focus:text-xs peer-focus:font-semibold peer-focus:text-[#DB4444]">
      {label}
    </span>
  </label>
);

const FloatingSelect = ({ id, name, label, className = '', onChange, value, options, required = false }) => (
  <label className={`relative block ${className}`}>
    <select
      id={id}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      className="peer block h-14 w-full appearance-none rounded-lg border border-slate-300 bg-transparent px-4 pb-2.5 pt-6 text-sm text-[#1A1A1A] outline-none transition-colors duration-500 focus:border-[#DB4444]"
    >
      <option value=""></option>
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
    <span
      className={`pointer-events-none absolute left-4 z-10 origin-[0] transform text-slate-500 transition-all duration-300 peer-focus:text-[#DB4444] ${
        value
          ? 'top-4 -translate-y-4 scale-75 text-xs font-semibold'
          : 'top-4 translate-y-0 scale-100 text-sm font-medium'
      }`}
    >
      {label}
    </span>
    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-slate-500">
      <FiChevronDown size={16} />
    </span>
  </label>
);

export default Checkout;
